import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu
} from '@mui/material';
import { ArrowBack, Add as AddIcon, Delete as DeleteIcon, Print as PrintIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { formatDateForInput, formatDateOnly } from '../utils/dateUtils';
import { formatQuantityDisplay } from '../utils/quantityDisplay';
import { packingSlipService } from '../services/packingSlipService';
import { orderService } from '../services/orderService';
import { vendorService } from '../services/vendorService';
import { customerInvoiceService } from '../services/customerInvoiceService';
import { addressService } from '../services/customerService';
import '../styles/print.css';
import PrintPage from '../components/print/PrintPage';
import { printWithFilename } from '../utils/printUtils';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';

const CARRIER_OPTIONS = ['UPS', 'USPS', 'DHL', 'FedEx', 'Customer Pickup', 'In-house carrier'];

/** Order / packing slip quantity (fractional OK, e.g. service hours). Normalizes float noise at 3 decimals. */
function parseWoQty(v) {
  if (v === '' || v == null) return 0;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, '').trim());
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 1000) / 1000;
}

/** Default ship-to for packing slip: saved shipping row, else default flag, else first address. */
function pickDefaultCustomerShipToAddress(addresses) {
  if (!Array.isArray(addresses) || addresses.length === 0) return null;
  return (
    addresses.find((a) => (a.address_type || '').toLowerCase() === 'shipping') ||
    addresses.find((a) => a.is_default) ||
    addresses[0]
  );
}

function packingSlipLabelFromAllocation(row) {
  if (!row) return '';
  const id = row.packing_slip_identifier != null && String(row.packing_slip_identifier).trim() !== ''
    ? String(row.packing_slip_identifier).trim()
    : null;
  if (id) return id;
  if (row.packing_slip_number != null && String(row.packing_slip_number).trim() !== '') {
    return String(row.packing_slip_number);
  }
  return row.packing_slip_id != null ? `#${row.packing_slip_id}` : '—';
}

const PackingSlipDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const workOrderIdParam = searchParams.get('work_order_id');
  const shipmentPurposeParam = searchParams.get('shipment_purpose');
  const printRequested = searchParams.get('print') === '1';
  const isNew = location.pathname.endsWith('/new') || id === 'new';

  const [order, setOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [slip, setSlip] = useState(null);
  const [linkedInvoice, setLinkedInvoice] = useState(null);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [savingShipTo, setSavingShipTo] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    work_order_id: workOrderIdParam || '',
    packing_slip_number: '',
    customer_id: '',
    shipped_date: format(new Date(), 'yyyy-MM-dd'),
    tracking_number: '',
    carrier: 'UPS',
    notes: '',
    po_number: '',
    ship_to_address_id: '',
    ship_to_address_line1: '',
    ship_to_address_line2: '',
    ship_to_city: '',
    ship_to_state: '',
    ship_to_zip_code: '',
    ship_to_country: '',
    shipment_purpose: shipmentPurposeParam === 'vendor_outbound' ? 'vendor_outbound' : 'customer',
    vendor_id: ''
  });
  const [items, setItems] = useState([]);
  const [orderLineItems, setOrderLineItems] = useState([]);
  const [packingAllocationsRows, setPackingAllocationsRows] = useState([]);

  const orderLineShipFingerprint = useMemo(
    () => (orderLineItems || []).map((l) => `${l.id}:${l.quantity_shipped ?? 0}`).join('|'),
    [orderLineItems]
  );

  /** Remaining qty for customer shipments (order line total minus qty already on other customer packing slips). */
  const customerOpenQty = useCallback((l) => {
    if (!l) return 0;
    const req = parseWoQty(l.quantity_required);
    const shipped = parseWoQty(l.quantity_shipped);
    return Math.max(0, Math.round((req - shipped) * 1000) / 1000);
  }, []);

  const packingAllocationsByLineId = useMemo(() => {
    const m = new Map();
    for (const row of packingAllocationsRows) {
      const key = String(row.work_order_item_id);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(row);
    }
    return m;
  }, [packingAllocationsRows]);

  useEffect(() => {
    if (!isNew || !form.work_order_id) {
      setPackingAllocationsRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await orderService.getCustomerPackingAllocations(form.work_order_id);
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) setPackingAllocationsRows(rows);
      } catch {
        if (!cancelled) setPackingAllocationsRows([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNew, form.work_order_id, orderLineShipFingerprint]);

  const hasAutoPrintedRef = useRef(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [creatingInvoiceFromSlip, setCreatingInvoiceFromSlip] = useState(false);

  const loadSlip = async () => {
    if (isNew) return;
    try {
      setLoading(true);
      const [res, ordersRes] = await Promise.all([
        packingSlipService.getById(id),
        orderService.getAll({ limit: 500 })
      ]);
      setOrders(ordersRes?.data ?? []);
      setSlip(res.data);
      const d = res.data;
      setForm({
        work_order_id: d.work_order_id != null ? String(d.work_order_id) : '',
        packing_slip_number: d.packing_slip_number ?? '',
        customer_id: d.customer_id ?? '',
        shipped_date: formatDateForInput(d.shipped_date) || '',
        tracking_number: d.tracking_number ?? '',
        carrier: d.carrier ?? '',
        notes: d.notes ?? '',
        po_number: d.po_number ?? '',
        ship_to_address_id: d.ship_to_address_id ? String(d.ship_to_address_id) : '',
        ship_to_address_line1: d.ship_to_address_line1 ?? '',
        ship_to_address_line2: d.ship_to_address_line2 ?? '',
        ship_to_city: d.ship_to_city ?? '',
        ship_to_state: d.ship_to_state ?? '',
        ship_to_zip_code: d.ship_to_zip_code ?? '',
        ship_to_country: d.ship_to_country ?? '',
        shipment_purpose: d.shipment_purpose === 'vendor_outbound' ? 'vendor_outbound' : 'customer',
        vendor_id: d.vendor_id != null ? String(d.vendor_id) : ''
      });
      setItems((d.items || []).map((i) => ({ ...i, work_order_item_id: i.work_order_item_id ?? '', part_id: i.part_id ?? '', quantity_shipped: i.quantity_shipped ?? 0, description: i.description ?? '' })));

      // Invoice that references this packing slip (optional).
      if (d.work_order_id) {
        try {
          const invoicesRes = await customerInvoiceService.getByWorkOrderId(d.work_order_id);
          const invoices = invoicesRes?.data || [];
          const match = invoices.find((inv) => String(inv.packing_slip_id) === String(d.id));
          setLinkedInvoice(match || null);
        } catch {
          setLinkedInvoice(null);
        }
      } else {
        setLinkedInvoice(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load packing slip');
    } finally {
      setLoading(false);
    }
  };

  const loadOrder = async (workOrderId) => {
    if (!workOrderId) return;
    try {
      const res = await orderService.getById(workOrderId);
      const ord = res.data?.data ?? res.data;
      setOrder(ord);
      setOrderLineItems(ord?.lineItems || []);
    } catch (err) {
      console.error('Load order:', err);
    }
  };

  useEffect(() => {
    if (isNew) {
      setForm((f) => ({
        ...f,
        work_order_id: workOrderIdParam || f.work_order_id,
        shipment_purpose: shipmentPurposeParam === 'vendor_outbound' ? 'vendor_outbound' : f.shipment_purpose || 'customer'
      }));
      if (workOrderIdParam) loadOrder(workOrderIdParam);
      packingSlipService.getNextNumber().then((r) => setForm((f) => ({ ...f, packing_slip_number: r.data?.packing_slip_number || f.packing_slip_number })));
      orderService.getAll({ limit: 500 }).then((r) => setOrders(r.data || []));
      vendorService.getAll({ limit: 500 }).then((r) => setVendors(r.data?.data || r.data || []));
      return;
    }
    loadSlip();
  }, [id, isNew, workOrderIdParam, shipmentPurposeParam]);

  useEffect(() => {
    if (!printRequested) return;
    if (isNew) return;
    if (!slip) return;
    if (hasAutoPrintedRef.current) return;
    hasAutoPrintedRef.current = true;
    // Wait a moment for the DOM to settle, then print.
    const slipFilename = slip?.identifier || slip?.packing_slip_number || 'Packing-Slip';
    printWithFilename(slipFilename, { delayMs: 500 });
  }, [printRequested, isNew, slip]);

  useEffect(() => {
    if (!printRequested) {
      hasAutoPrintedRef.current = false;
    }
  }, [printRequested]);

  // When creating a slip, default packing slip PO from the selected work order (user can clear or override).
  useEffect(() => {
    if (!isNew || !order?.id) return;
    setForm((f) => ({ ...f, po_number: order.po_number ?? '' }));
  }, [order?.id, isNew]);

  useEffect(() => {
    if (form.work_order_id) loadOrder(form.work_order_id);
  }, [form.work_order_id]);

  useEffect(() => {
    const customerId = form.customer_id || order?.customer_id || slip?.customer_id;
    if (!customerId) {
      setCustomerAddresses([]);
      return;
    }
    let cancelled = false;
    addressService.getByCustomerId(customerId)
      .then((res) => {
        if (cancelled) return;
        setCustomerAddresses(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setCustomerAddresses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [form.customer_id, order?.customer_id, slip?.customer_id]);

  // New customer shipment: auto-select default saved ship-to so users can submit with defaults.
  useEffect(() => {
    if (!isNew) return;
    if (form.shipment_purpose !== 'customer') return;
    if (!customerAddresses.length) return;
    setForm((f) => {
      if (f.shipment_purpose !== 'customer') return f;
      const idOk =
        f.ship_to_address_id &&
        customerAddresses.some((a) => String(a.id) === String(f.ship_to_address_id));
      if (idOk) return f;
      const def = pickDefaultCustomerShipToAddress(customerAddresses);
      if (!def) return f;
      return {
        ...f,
        ship_to_address_id: String(def.id),
        ship_to_address_line1: def.address_line1 ?? '',
        ship_to_address_line2: def.address_line2 ?? '',
        ship_to_city: def.city ?? '',
        ship_to_state: def.state ?? '',
        ship_to_zip_code: def.zip_code ?? '',
        ship_to_country: def.country ?? ''
      };
    });
  }, [isNew, form.shipment_purpose, customerAddresses]);

  // Auto-populate customer from work order (new slip or when editing slip with missing customer)
  useEffect(() => {
    if (order && order.customer_id) {
      setForm((f) => {
        if (f.customer_id) return f; // already set
        return { ...f, customer_id: order.customer_id };
      });
    }
  }, [order]);

  // Prefill packing slip lines when creating: all order lines with required > 0. Customer: default qty = remaining after other slips (0 if fully on other slips). Vendor: default = full required.
  useEffect(() => {
    if (!isNew) return;
    if (!orderLineItems.length) {
      setItems([]);
      return;
    }
    const isVendor = form.shipment_purpose === 'vendor_outbound';
    const source = orderLineItems.filter((l) => parseWoQty(l.quantity_required) > 0);
    const lines = source.map((l) => {
      const req = parseWoQty(l.quantity_required);
      const open = customerOpenQty(l);
      const defaultQty = isVendor ? req : open;
      return {
        work_order_item_id: l.id,
        part_id: l.part_id ?? '',
        quantity_shipped: defaultQty,
        description: l.description ?? '',
        part_number: l.item_type === 'service' ? effectiveServiceCode(l) || '' : l.part_number ?? '',
        part_name: l.part_name ?? l.service_name ?? ''
      };
    });
    setItems(lines);
  }, [isNew, orderLineItems, form.shipment_purpose, customerOpenQty]);

  // Prefill ship-to from work order when creating new slip only if no saved customer addresses (they get a default select above).
  useEffect(() => {
    if (!isNew || !order) return;
    if (form.shipment_purpose !== 'customer') return;
    setForm((f) => {
      if (f.ship_to_address_id || String(f.ship_to_address_line1 || '').trim()) return f;
      if (customerAddresses.length > 0) return f;
      return {
        ...f,
        ship_to_address_line1: order.address_line1 ?? '',
        ship_to_address_line2: order.address_line2 ?? '',
        ship_to_city: order.city ?? '',
        ship_to_state: order.state ?? '',
        ship_to_zip_code: order.zip_code ?? '',
        ship_to_country: order.country ?? ''
      };
    });
  }, [isNew, order?.id, order?.address_line1, form.shipment_purpose, customerAddresses.length]);

  // Packing slips are immutable once created; allow editing only during creation.
  const canEditAll = isNew;
  const canEditItems = isNew;

  const [addLineAnchor, setAddLineAnchor] = useState(null);

  const addLine = (woItem) => {
    setAddLineAnchor(null);
    if (!woItem) return;
    const isVendor = form.shipment_purpose === 'vendor_outbound';
    const open = customerOpenQty(woItem);
    if (!isVendor && open <= 0) return;
    const req = parseWoQty(woItem.quantity_required);
    const defaultQty = isVendor ? req : open;
    setItems((prev) => [...prev, {
      work_order_item_id: woItem.id,
      part_id: woItem.part_id ?? '',
      part_number: woItem.item_type === 'service' ? effectiveServiceCode(woItem) || '' : woItem.part_number ?? '',
      part_name: woItem.part_name ?? woItem.service_name ?? '',
      quantity_shipped: defaultQty,
      description: woItem.description ?? ''
    }]);
  };

  const updateLine = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      if (field === 'quantity_shipped') {
        const current = next[index];
        const woLine = orderLineItems.find((l) => String(l.id) === String(current.work_order_item_id));
        const requiredQty = parseWoQty(woLine?.quantity_required);
        const alreadyShippedQty = parseWoQty(woLine?.quantity_shipped);
        const vendorOutbound = form.shipment_purpose === 'vendor_outbound';
        const maxQty = vendorOutbound
          ? requiredQty
          : Math.max(0, Math.round((requiredQty - alreadyShippedQty) * 1000) / 1000);
        const parsed = Math.max(0, parseWoQty(value));
        const capped = Math.min(parsed, maxQty);
        next[index] = { ...current, [field]: Math.round(capped * 1000) / 1000 };
      } else {
        next[index] = { ...next[index], [field]: value };
      }
      return next;
    });
  };

  const removeLine = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const goAfterCreatePackingSlip = useCallback(
    (res, createPayload) => {
      const slipId = res?.data?.id ?? res?.data;
      const woId = createPayload?.work_order_id;
      if (woId) {
        navigate(`/work-orders/${woId}`, { replace: true });
      } else if (slipId) {
        navigate(`/packing-slips/${slipId}`, { replace: true });
      } else {
        navigate('/packing-slips', { replace: true });
      }
    },
    [navigate]
  );

  const handleSave = async () => {
    if (!isNew) {
      setError('Packing slips cannot be modified.');
      return;
    }
    if (!form.work_order_id) {
      setError('Work order is required.');
      return;
    }
    const isVendorOutbound = form.shipment_purpose === 'vendor_outbound';
    const overShippedLine = items.find((i) => {
      const woLine = orderLineItems.find((l) => String(l.id) === String(i.work_order_item_id));
      const requiredQty = parseWoQty(woLine?.quantity_required);
      const alreadyShippedQty = parseWoQty(woLine?.quantity_shipped);
      const openToShipQty = isVendorOutbound
        ? requiredQty
        : Math.max(0, Math.round((requiredQty - alreadyShippedQty) * 1000) / 1000);
      const shippedQty = parseWoQty(i.quantity_shipped);
      return shippedQty > openToShipQty + 1e-6;
    });
    if (overShippedLine) {
      const woLine = orderLineItems.find((l) => String(l.id) === String(overShippedLine.work_order_item_id));
      const requiredQty = parseWoQty(woLine?.quantity_required);
      const alreadyShippedQty = parseWoQty(woLine?.quantity_shipped);
      const openToShipQty = isVendorOutbound
        ? requiredQty
        : Math.max(0, Math.round((requiredQty - alreadyShippedQty) * 1000) / 1000);
      const shippedQty = parseWoQty(overShippedLine.quantity_shipped);
      if (!isVendorOutbound && openToShipQty === 0 && shippedQty > 0) {
        setError('That order line is already fully shipped on another packing slip. Remove it from this slip.');
      } else {
        setError('Qty to ship cannot exceed remaining quantity for the order line.');
      }
      return;
    }
    const validItems = items.filter((i) => {
      if (!(i.work_order_item_id || i.part_id) || Number(i.quantity_shipped) <= 0) return false;
      if (!isVendorOutbound) {
        const woLine = orderLineItems.find((l) => String(l.id) === String(i.work_order_item_id));
        if (woLine && customerOpenQty(woLine) <= 0) return false;
      }
      return true;
    });
    if (validItems.length === 0) {
      setError('At least one line item with Qty to ship greater than zero is required.');
      return;
    }
    setError('');
    const payload = {
      work_order_id: parseInt(form.work_order_id, 10),
      packing_slip_number: form.packing_slip_number,
      customer_id: form.customer_id ? parseInt(form.customer_id, 10) : null,
      shipped_date:
        form.shipped_date && String(form.shipped_date).trim() !== ''
          ? form.shipped_date
          : format(new Date(), 'yyyy-MM-dd'),
      tracking_number: form.tracking_number || null,
      carrier: form.carrier || null,
      notes: form.notes || null,
      po_number: String(form.po_number ?? '').trim() || null,
      ship_to_address_id: form.ship_to_address_id ? parseInt(form.ship_to_address_id, 10) : null,
      ship_to_address_line1: form.ship_to_address_line1 || null,
      ship_to_address_line2: form.ship_to_address_line2 || null,
      ship_to_city: form.ship_to_city || null,
      ship_to_state: form.ship_to_state || null,
      ship_to_zip_code: form.ship_to_zip_code || null,
      ship_to_country: form.ship_to_country || null,
      shipment_purpose: form.shipment_purpose || 'customer',
      vendor_id: form.vendor_id ? parseInt(form.vendor_id, 10) : null,
      items: validItems.map((i) => ({
        work_order_item_id: i.work_order_item_id ? parseInt(i.work_order_item_id, 10) : null,
        part_id: i.part_id ? parseInt(i.part_id, 10) : null,
        quantity_shipped: parseWoQty(i.quantity_shipped),
        description: i.description || null
      }))
    };
    try {
      setSaving(true);
      const res = await packingSlipService.create(payload);
      goAfterCreatePackingSlip(res, payload);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInvoiceFromSlip = async () => {
    if (!slip?.id || linkedInvoice?.id) return;
    try {
      setCreatingInvoiceFromSlip(true);
      setError('');
      await customerInvoiceService.createFromPackingSlip(slip.id);
      await loadSlip();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setCreatingInvoiceFromSlip(false);
    }
  };

  if (loading && !isNew) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress />
      </Box>
    );
  }

  const handlePrint = () => {
    const slipFilename = slip?.identifier || slip?.packing_slip_number || 'Packing-Slip';
    printWithFilename(slipFilename);
  };

  const handleDeleteSlip = async () => {
    if (isNew || !id) return;
    try {
      setDeleting(true);
      setError('');
      await packingSlipService.delete(id);
      setDeleteConfirmOpen(false);
      if (slip?.work_order_id) {
        navigate(`/work-orders/${slip.work_order_id}`, { replace: true });
      } else {
        navigate('/packing-slips', { replace: true });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete packing slip');
    } finally {
      setDeleting(false);
    }
  };

  const prefillShipToFromOrder = () => ({
    ship_to_address_line1: order?.address_line1 ?? '',
    ship_to_address_line2: order?.address_line2 ?? '',
    ship_to_city: order?.city ?? '',
    ship_to_state: order?.state ?? '',
    ship_to_zip_code: order?.zip_code ?? '',
    ship_to_country: order?.country ?? ''
  });

  const handleShipToAddressSelect = (addressIdValue) => {
    if (!addressIdValue) {
      setForm((f) => ({
        ...f,
        ship_to_address_id: '',
        ...prefillShipToFromOrder()
      }));
      return;
    }
    const selected = customerAddresses.find((a) => String(a.id) === String(addressIdValue));
    setForm((f) => ({
      ...f,
      ship_to_address_id: String(addressIdValue),
      ship_to_address_line1: selected?.address_line1 ?? '',
      ship_to_address_line2: selected?.address_line2 ?? '',
      ship_to_city: selected?.city ?? '',
      ship_to_state: selected?.state ?? '',
      ship_to_zip_code: selected?.zip_code ?? '',
      ship_to_country: selected?.country ?? ''
    }));
  };

  const handleSaveShipToAddress = async () => {
    if (isNew || !slip?.id) return;
    try {
      setSavingShipTo(true);
      setError('');
      const payload = {
        ship_to_address_id: form.ship_to_address_id ? parseInt(form.ship_to_address_id, 10) : null,
        ship_to_address_line1: form.ship_to_address_line1 || null,
        ship_to_address_line2: form.ship_to_address_line2 || null,
        ship_to_city: form.ship_to_city || null,
        ship_to_state: form.ship_to_state || null,
        ship_to_zip_code: form.ship_to_zip_code || null,
        ship_to_country: form.ship_to_country || null
      };
      const res = await packingSlipService.update(slip.id, payload);
      const updated = res.data;
      setSlip(updated);
      setForm((f) => ({
        ...f,
        ship_to_address_id: updated?.ship_to_address_id ? String(updated.ship_to_address_id) : '',
        ship_to_address_line1: updated?.ship_to_address_line1 ?? '',
        ship_to_address_line2: updated?.ship_to_address_line2 ?? '',
        ship_to_city: updated?.ship_to_city ?? '',
        ship_to_state: updated?.ship_to_state ?? '',
        ship_to_zip_code: updated?.ship_to_zip_code ?? '',
        ship_to_country: updated?.ship_to_country ?? ''
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update ship-to address');
    } finally {
      setSavingShipTo(false);
    }
  };

  return (
    <Box>
      {/* Print-friendly packing list - only visible when printing */}
      {!isNew && slip && (
        <PrintPage
          title={slip?.shipment_purpose === 'vendor_outbound' ? 'Vendor outbound shipment' : 'Packing List'}
          showSpacer={false}
        >
          <div className="print-two-column" style={{ marginBottom: 16 }}>
            <div className="print-customer-info">
              <h3>Bill To:</h3>
              <div>
                {(order?.company_name || slip?.customer_name || 'Customer')}<br />
                {order?.address_line1 && <>{order.address_line1}<br /></>}
                {order?.address_line2 && <>{order.address_line2}<br /></>}
                {order?.city && <>{order.city}, </>}{order?.state || ''} {order?.zip_code || ''}
              </div>
            </div>
            <div className="print-customer-info">
              <h3>Ship To:</h3>
              <div>
                {[
                  slip?.ship_to_address_line1,
                  slip?.ship_to_address_line2,
                  [slip?.ship_to_city, slip?.ship_to_state, slip?.ship_to_zip_code].filter(Boolean).join(' ').trim(),
                  slip?.ship_to_country
                ].filter(Boolean).join(', ') || `To: ${slip?.customer_name || order?.company_name || '—'}`}
              </div>
            </div>
          </div>

          <div style={{ border: '2px solid #000', padding: 12, marginBottom: 16 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
              <div>
                <div style={{ fontSize: 11, marginBottom: 2 }}>Shipment No:</div>
                <div style={{ fontWeight: 600 }}>{slip.identifier ?? slip.packing_slip_number ?? slip.id}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, marginBottom: 2 }}>Shipment Date:</div>
                <div>{formatDateOnly(form.shipped_date || slip.shipped_date) || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, marginBottom: 2 }}>Ship Via:</div>
                <div>{form.carrier || slip.carrier || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, marginBottom: 2 }}>Order Number:</div>
                <div style={{ fontWeight: 600 }}>{order?.identifier ?? order?.work_order_identifier ?? order?.work_order_number ?? slip?.work_order_id ?? '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, marginBottom: 2 }}>Order Date:</div>
                <div>{formatDateOnly(order?.order_date) || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, marginBottom: 2 }}>Customer Code:</div>
                <div>{order?.customer_code || slip?.customer_name || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, marginBottom: 2 }}>PO Number:</div>
                <div>
                  {slip?.po_number != null && String(slip.po_number).trim() !== ''
                    ? String(slip.po_number).trim()
                    : order?.po_number || '—'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, marginBottom: 2 }}>Terms:</div>
                <div>{order?.terms || '—'}</div>
              </div>
            </div>
          </div>

          <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', backgroundColor: '#f0f0f0' }}>Item</th>
                <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', backgroundColor: '#f0f0f0' }}>Ordered</th>
                <th style={{ border: '1px solid #000', padding: 8, textAlign: 'center', backgroundColor: '#f0f0f0' }}>Shipped</th>
                <th style={{ border: '1px solid #000', padding: 8, textAlign: 'left', backgroundColor: '#f0f0f0' }}>Description</th>
              </tr>
            </thead>
            <tbody>
              {items.filter((i) => (i.work_order_item_id || i.part_id) && Number(i.quantity_shipped) > 0).map((row, idx) => {
                const woLine = orderLineItems.find((l) => String(l.id) === String(row.work_order_item_id));
                const ordered = woLine?.quantity_required ?? row.quantity_required ?? '—';
                return (
                  <tr key={idx}>
                    <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>{idx + 1}</td>
                    <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>
                      {ordered === '—' ? '—' : formatQuantityDisplay(ordered ?? 0)}
                    </td>
                    <td style={{ border: '1px solid #000', padding: 8, textAlign: 'center' }}>
                      {formatQuantityDisplay(row.quantity_shipped ?? 0)}
                    </td>
                    <td style={{ border: '1px solid #000', padding: 8 }}>{row.description || row.part_number || row.part_name || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 32, paddingTop: 16, borderTop: '1px solid #ccc', fontSize: 11 }}>
            <div>
              <div>Packing Clerk&apos;s Signature</div>
              <div style={{ borderBottom: '1px solid #000', width: 180, height: 20, marginTop: 4 }} />
              <div style={{ marginTop: 4 }}>Ledger</div>
              <div style={{ marginTop: 8, color: '#666' }}>Report Generated: {format(new Date(), 'MM/dd/yyyy h:mm:ss a')}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div>Received In Good Order By</div>
              <div style={{ borderBottom: '1px solid #000', width: 180, height: 20, marginTop: 4 }} />
              <div style={{ marginTop: 4 }}>{slip?.customer_name || order?.company_name || '—'}</div>
              <div style={{ marginTop: 8 }}>Page 1 of 1</div>
            </div>
          </div>
        </PrintPage>
      )}

      <Box className="no-print">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton size="small" onClick={() => navigate(slip?.work_order_id ? `/packing-slips?work_order_id=${slip.work_order_id}` : '/packing-slips')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          {isNew ? 'New packing slip' : `Packing slip ${slip?.identifier ?? slip?.packing_slip_number ?? id}`}
        </Typography>
        {(isNew && form.shipment_purpose === 'vendor_outbound') || (!isNew && slip?.shipment_purpose === 'vendor_outbound') ? (
          <Chip size="small" label="Vendor outbound" color="secondary" sx={{ fontWeight: 600 }} />
        ) : null}
        {!isNew && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Packing slip is read-only.
          </Typography>
        )}
        {!isNew && slip && (
          <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ ml: 1 }}>
            Print packing list
          </Button>
        )}
        {!isNew && slip && (
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteConfirmOpen(true)}
          >
            Delete
          </Button>
        )}
      </Box>

      {/* Summary / Information bar (same style as other pages) */}
      <Paper sx={{ p: 1.5, mb: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {isNew ? (
            <>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Order #</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {order?.id ? (
                    <Button
                      component={RouterLink}
                      to={`/work-orders/${order.id}`}
                      variant="text"
                      size="small"
                      sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 600 }}
                    >
                      {order?.identifier ?? order?.work_order_identifier ?? order?.work_order_number ?? order?.id}
                    </Button>
                  ) : (
                    order?.identifier ?? order?.work_order_identifier ?? order?.work_order_number ?? order?.id ?? '—'
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
                <Typography variant="body2">
                  {order?.customer_id ? (
                    <Button
                      component={RouterLink}
                      to={`/customers/${order.customer_id}`}
                      variant="text"
                      size="small"
                      sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 400 }}
                    >
                      {order?.company_name || (order?.customer_first_name || order?.customer_last_name ? [order.customer_first_name, order.customer_last_name].filter(Boolean).join(' ') : null) || `#${order.customer_id}`}
                    </Button>
                  ) : (
                    order?.company_name || (order?.customer_first_name || order?.customer_last_name ? [order.customer_first_name, order.customer_last_name].filter(Boolean).join(' ') : null) || '—'
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer PO</Typography>
                <Typography variant="body2">{order?.po_number || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Packing slip PO</Typography>
                <Typography variant="body2">{String(form.po_number || '').trim() || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Ship to</Typography>
                <Typography variant="body2" sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[slip?.ship_to_address_line1, slip?.ship_to_city, slip?.ship_to_state].filter(Boolean).join(', ') ||
                    (order ? [order.address_line1, order.city, order.state].filter(Boolean).join(', ') || '—' : '—')}
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Packing Slip #</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{slip?.identifier ?? slip?.packing_slip_number ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Work Order</Typography>
                {slip?.work_order_id ? (
                  <Button
                    component={RouterLink}
                    to={`/work-orders/${slip.work_order_id}`}
                    variant="text"
                    size="small"
                    sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 400 }}
                  >
                    {slip?.work_order_identifier ?? slip?.work_order_number ?? slip?.work_order_id}
                  </Button>
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
                <Typography variant="body2">
                  {slip?.customer_id ? (
                    <Button
                      component={RouterLink}
                      to={`/customers/${slip.customer_id}`}
                      variant="text"
                      size="small"
                      sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 400 }}
                    >
                      {slip?.customer_name ?? `#${slip.customer_id}`}
                    </Button>
                  ) : (
                    slip?.customer_name ?? '—'
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer PO</Typography>
                <Typography variant="body2">{order?.po_number || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Packing slip PO</Typography>
                <Typography variant="body2">
                  {slip?.po_number != null && String(slip.po_number).trim() !== ''
                    ? String(slip.po_number).trim()
                    : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Ship Date</Typography>
                <Typography variant="body2">{formatDateOnly(slip?.shipped_date || form.shipped_date) || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Date Generated</Typography>
                <Typography variant="body2">{formatDateOnly(slip?.created_at) || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice #</Typography>
                <Typography variant="body2">
                  {linkedInvoice?.id ? (
                    <Button
                      component={RouterLink}
                      to={`/customer-invoices/${linkedInvoice.id}`}
                      variant="text"
                      size="small"
                      sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 400 }}
                    >
                      {linkedInvoice.identifier ?? linkedInvoice.invoice_number ?? `#${linkedInvoice.id}`}
                    </Button>
                  ) : !isNew && slip?.id ? (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCreateInvoiceFromSlip}
                      disabled={creatingInvoiceFromSlip}
                      sx={{ mt: 0.25 }}
                    >
                      {creatingInvoiceFromSlip ? 'Creating…' : 'Create invoice'}
                    </Button>
                  ) : (
                    '—'
                  )}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Carrier</Typography>
                <Typography variant="body2">{(form.carrier || slip?.carrier) ?? '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Tracking</Typography>
                <Typography variant="body2">{(form.tracking_number || slip?.tracking_number) ?? '—'}</Typography>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Customer</Typography>
            <Typography variant="body2">
              {(order?.customer_id || slip?.customer_id) ? (
                <Button
                  component={RouterLink}
                  to={`/customers/${order?.customer_id ?? slip?.customer_id}`}
                  variant="text"
                  size="small"
                  sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 400 }}
                >
                  {order?.company_name || (order?.customer_first_name || order?.customer_last_name ? [order.customer_first_name, order.customer_last_name].filter(Boolean).join(' ') : null) || slip?.customer_name || `#${order?.customer_id ?? slip?.customer_id}`}
                </Button>
              ) : (
                order?.company_name || (order?.customer_first_name || order?.customer_last_name ? [order.customer_first_name, order.customer_last_name].filter(Boolean).join(' ') : null) || slip?.customer_name || '—'
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Shipping address</Typography>
            {customerAddresses.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 280 }}>
                  <InputLabel>Address</InputLabel>
                  <Select
                    value={form.ship_to_address_id || ''}
                    label="Address"
                    onChange={(e) => handleShipToAddressSelect(e.target.value)}
                  >
                    <MenuItem value="">
                      {isNew ? 'Order / type below' : 'Use order address or lines below'}
                    </MenuItem>
                    {customerAddresses.map((addr) => (
                      <MenuItem key={addr.id} value={String(addr.id)}>
                        {[
                          (addr.address_type || '').toLowerCase() === 'shipping' ? 'Ship: ' : '',
                          addr.address_line1,
                          addr.address_line2,
                          [addr.city, addr.state, addr.zip_code].filter(Boolean).join(' ')
                        ].filter(Boolean).join(' ') || `Address #${addr.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {!isNew && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSaveShipToAddress}
                    disabled={savingShipTo}
                  >
                    {savingShipTo ? 'Saving...' : 'Save Ship-To'}
                  </Button>
                )}
              </Box>
            ) : null}
            {isNew ? (
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Address line 1"
                    value={form.ship_to_address_line1}
                    onChange={(e) => setForm((f) => ({ ...f, ship_to_address_line1: e.target.value, ship_to_address_id: '' }))}
                    disabled={!canEditAll}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Address line 2"
                    value={form.ship_to_address_line2}
                    onChange={(e) => setForm((f) => ({ ...f, ship_to_address_line2: e.target.value, ship_to_address_id: '' }))}
                    disabled={!canEditAll}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="City"
                    value={form.ship_to_city}
                    onChange={(e) => setForm((f) => ({ ...f, ship_to_city: e.target.value, ship_to_address_id: '' }))}
                    disabled={!canEditAll}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="State"
                    value={form.ship_to_state}
                    onChange={(e) => setForm((f) => ({ ...f, ship_to_state: e.target.value, ship_to_address_id: '' }))}
                    disabled={!canEditAll}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ZIP"
                    value={form.ship_to_zip_code}
                    onChange={(e) => setForm((f) => ({ ...f, ship_to_zip_code: e.target.value, ship_to_address_id: '' }))}
                    disabled={!canEditAll}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Country"
                    value={form.ship_to_country}
                    onChange={(e) => setForm((f) => ({ ...f, ship_to_country: e.target.value, ship_to_address_id: '' }))}
                    disabled={!canEditAll}
                  />
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2">
                {[
                  form.ship_to_address_line1 || slip?.ship_to_address_line1,
                  form.ship_to_address_line2 || slip?.ship_to_address_line2,
                  [
                    form.ship_to_city || slip?.ship_to_city,
                    form.ship_to_state || slip?.ship_to_state,
                    form.ship_to_zip_code || slip?.ship_to_zip_code
                  ].filter(Boolean).join(' ').trim(),
                  form.ship_to_country || slip?.ship_to_country
                ].filter(Boolean).join(', ') || [
                  order?.address_line1,
                  order?.address_line2,
                  [order?.city, order?.state, order?.zip_code].filter(Boolean).join(' ').trim()
                ].filter(Boolean).join(', ') || '—'}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {isNew ? (
              <FormControl fullWidth size="small">
                <InputLabel>Work Order</InputLabel>
                <Select
                  value={form.work_order_id}
                  label="Work Order"
                  onChange={(e) => setForm((f) => ({ ...f, work_order_id: e.target.value }))}
                >
                  <MenuItem value="">Select</MenuItem>
                  {orders.map((o) => (
                    <MenuItem key={o.id} value={String(o.id)}>{o.identifier ?? o.work_order_identifier ?? o.work_order_number ?? o.id}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Work Order"
                value={slip?.work_order_identifier ?? slip?.work_order_number ?? order?.identifier ?? form.work_order_id ?? '—'}
                InputProps={{ readOnly: true }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {isNew ? (
              <TextField
                fullWidth
                size="small"
                label="Packing Slip"
                value={form.packing_slip_number}
                onChange={(e) => setForm((f) => ({ ...f, packing_slip_number: e.target.value }))}
                disabled={!canEditAll}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Packing Slip"
                value={slip?.identifier ?? form.packing_slip_number ?? '—'}
                InputProps={{ readOnly: true }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            {isNew ? (
              <TextField
                fullWidth
                size="small"
                label="PO number (optional)"
                value={form.po_number}
                onChange={(e) => setForm((f) => ({ ...f, po_number: e.target.value }))}
                disabled={!canEditAll}
                helperText="Shown on printed packing list; defaults from order PO"
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                label="PO number (packing slip)"
                value={slip?.po_number != null && String(slip.po_number).trim() !== '' ? String(slip.po_number).trim() : '—'}
                InputProps={{ readOnly: true }}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Ship Date"
              type="date"
              value={form.shipped_date}
              onChange={(e) => setForm((f) => ({ ...f, shipped_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              helperText={isNew ? 'Defaults to today; saved as shipped when you create' : 'Shipment date'}
              disabled={!canEditAll}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              freeSolo
              fullWidth
              size="small"
              options={CARRIER_OPTIONS}
              value={form.carrier || null}
              onChange={(e, newValue) => setForm((f) => ({ ...f, carrier: newValue ?? '' }))}
              onInputChange={(e, newInputValue) => setForm((f) => ({ ...f, carrier: newInputValue }))}
              renderInput={(params) => <TextField {...params} label="Carrier" />}
              disabled={!canEditAll}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Tracking"
              value={form.tracking_number}
              onChange={(e) => setForm((f) => ({ ...f, tracking_number: e.target.value }))}
              disabled={!canEditAll}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Notes"
              multiline
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              disabled={!canEditAll}
            />
          </Grid>
          {isNew && (
            <>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Shipment purpose</InputLabel>
                  <Select
                    label="Shipment purpose"
                    value={form.shipment_purpose || 'customer'}
                    onChange={(e) => setForm((f) => ({ ...f, shipment_purpose: e.target.value }))}
                  >
                    <MenuItem value="customer">Customer shipment</MenuItem>
                    <MenuItem value="vendor_outbound">Vendor / contractor outbound</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={form.shipment_purpose !== 'vendor_outbound'}>
                  <InputLabel>Vendor / contractor</InputLabel>
                  <Select
                    label="Vendor / contractor"
                    value={form.vendor_id || ''}
                    onChange={(e) => setForm((f) => ({ ...f, vendor_id: e.target.value }))}
                  >
                    <MenuItem value="">None</MenuItem>
                    {vendors.map((v) => (
                      <MenuItem key={v.id} value={String(v.id)}>
                        {v.name || v.vendor_number || v.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
          {!isNew && slip?.shipment_purpose === 'vendor_outbound' && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Vendor / contractor: {slip.vendor_name || '—'}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, pl: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Line items</Typography>
        {isNew &&
          form.work_order_id &&
          form.shipment_purpose !== 'vendor_outbound' &&
          orderLineItems.length > 0 &&
          orderLineItems.some((l) => parseWoQty(l.quantity_required) > 0) &&
          orderLineItems.filter((l) => parseWoQty(l.quantity_required) > 0).every((l) => customerOpenQty(l) <= 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              All quantities are already on other packing slips. Lines below are shown for reference; Qty to Ship stays 0 until something is left to ship.
            </Typography>
          )}
        {canEditItems && form.work_order_id && orderLineItems.length > 0 && (() => {
          const isVendor = form.shipment_purpose === 'vendor_outbound';
          const addable = orderLineItems.filter((l) => {
            if (items.some((row) => String(row.work_order_item_id) === String(l.id))) return false;
            if (isVendor) return parseWoQty(l.quantity_required) > 0;
            return customerOpenQty(l) > 0;
          });
          return (
            <>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={(e) => setAddLineAnchor(e.currentTarget)}
                sx={{ mb: 1 }}
                disabled={addable.length === 0}
              >
                Add line
              </Button>
              <Menu anchorEl={addLineAnchor} open={!!addLineAnchor} onClose={() => setAddLineAnchor(null)}>
                {addable.map((l) => (
                  <MenuItem key={l.id} onClick={() => addLine(l)}>
                    {`${(l.item_type === 'service' ? effectiveServiceCode(l) : l.part_number) ?? l.part_name ?? l.service_name ?? l.id} – ${l.part_name ?? l.service_name ?? l.part_number ?? '—'} (ordered: ${formatQuantityDisplay(l.quantity_required ?? 0)}${!isVendor ? `, left to ship: ${customerOpenQty(l)}` : ''})`}
                  </MenuItem>
                ))}
              </Menu>
            </>
          );
        })()}
        <Box sx={{ width: '95%', maxWidth: 900 }}>
          <Table
            size="small"
            sx={{
              '& .MuiTableCell-root': { py: 0.5, px: 0.75, fontSize: '0.8125rem', verticalAlign: 'middle' },
              '& .MuiTableCell-head': { py: 0.5, fontSize: '0.75rem', fontWeight: 600 },
              '& .MuiTextField-root .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Part #</TableCell>
                <TableCell>Part</TableCell>
                <TableCell align="right">Ordered</TableCell>
                {canEditItems && form.shipment_purpose !== 'vendor_outbound' && (
                  <>
                    <TableCell align="right">Packing Slip</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Status</TableCell>
                  </>
                )}
                <TableCell align="right">{canEditItems ? 'Qty to Ship' : 'Shipped'}</TableCell>
                {canEditItems && <TableCell width={50} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row, idx) => {
                const woLine = orderLineItems.find((l) => String(l.id) === String(row.work_order_item_id));
                const requiredQty = parseWoQty(woLine?.quantity_required);
                const alreadyShippedQty = parseWoQty(woLine?.quantity_shipped);
                const isVendor = form.shipment_purpose === 'vendor_outbound';
                const maxQty = isVendor
                  ? requiredQty
                  : Math.max(0, Math.round((requiredQty - alreadyShippedQty) * 1000) / 1000);
                const openCustomer = woLine ? customerOpenQty(woLine) : 0;
                const lineLockedOnNewCustomer = canEditItems && !isVendor && openCustomer <= 0;
                const lineAllocations = woLine
                  ? packingAllocationsByLineId.get(String(woLine.id)) || []
                  : [];
                return (
                  <TableRow
                    key={idx}
                    sx={lineLockedOnNewCustomer ? { bgcolor: 'action.hover', opacity: 0.92 } : undefined}
                  >
                    <TableCell>
                      {woLine?.item_type === 'service'
                        ? effectiveServiceCode(woLine) || row.part_number || '—'
                        : row.part_number ?? woLine?.part_number ?? '—'}
                    </TableCell>
                    <TableCell>{row.part_name ?? woLine?.part_name ?? woLine?.service_name ?? '—'}</TableCell>
                    <TableCell align="right">
                      {woLine?.quantity_required != null || row.quantity_required != null
                        ? formatQuantityDisplay(woLine?.quantity_required ?? row.quantity_required ?? 0)
                        : '—'}
                    </TableCell>
                    {canEditItems && !isVendor && (
                      <>
                        {lineAllocations.length === 0 && alreadyShippedQty > 0 ? (
                          <TableCell colSpan={3} align="right">
                            <Typography variant="caption" color="text.secondary">
                              Total: {formatQuantityDisplay(alreadyShippedQty)} (detail unavailable)
                            </Typography>
                          </TableCell>
                        ) : lineAllocations.length === 0 ? (
                          <>
                            <TableCell align="right">—</TableCell>
                            <TableCell align="right">—</TableCell>
                            <TableCell align="right">—</TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell align="right" sx={{ verticalAlign: 'top', maxWidth: 200 }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end' }}>
                                {lineAllocations.map((alloc, ai) => (
                                  <Button
                                    key={`${alloc.packing_slip_id}-slip-${ai}`}
                                    component={RouterLink}
                                    to={`/packing-slips/${alloc.packing_slip_id}`}
                                    size="small"
                                    variant="text"
                                    sx={{
                                      p: 0,
                                      minWidth: 0,
                                      textTransform: 'none',
                                      fontSize: '0.8125rem',
                                      fontWeight: 600,
                                      justifyContent: 'flex-end'
                                    }}
                                  >
                                    {packingSlipLabelFromAllocation(alloc)}
                                  </Button>
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end' }}>
                                {lineAllocations.map((alloc, ai) => (
                                  <Typography
                                    key={`${alloc.packing_slip_id}-qty-${ai}`}
                                    variant="body2"
                                    sx={{ minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
                                  >
                                    {alloc.quantity_shipped ?? 0}
                                  </Typography>
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-end' }}>
                                {lineAllocations.map((alloc, ai) => (
                                  <Box
                                    key={`${alloc.packing_slip_id}-st-${ai}`}
                                    sx={{
                                      minHeight: 28,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'flex-end',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: alloc.shipped_date ? 'success.dark' : 'text.secondary',
                                        textTransform: 'lowercase'
                                      }}
                                    >
                                      {alloc.shipped_date ? 'shipped' : 'not shipped'}
                                    </Typography>
                                    {alloc.shipped_date ? (
                                      <Typography variant="caption" color="text.secondary">
                                        {formatDateOnly(alloc.shipped_date)}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                ))}
                              </Box>
                            </TableCell>
                          </>
                        )}
                      </>
                    )}
                    <TableCell align="right">
                      {canEditItems ? (
                        <TextField
                          type="number"
                          size="small"
                          value={lineLockedOnNewCustomer ? 0 : row.quantity_shipped}
                          onChange={(e) => updateLine(idx, 'quantity_shipped', e.target.value)}
                          inputProps={{ min: 0, max: maxQty, step: 'any' }}
                          sx={{ width: 80 }}
                          disabled={lineLockedOnNewCustomer}
                          title={
                            lineLockedOnNewCustomer
                              ? 'Fully committed on other packing slips — cannot ship again on this slip'
                              : undefined
                          }
                        />
                      ) : (
                        row.quantity_shipped ?? 0
                      )}
                    </TableCell>
                    {canEditItems && (
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => removeLine(idx)}
                          disabled={lineLockedOnNewCustomer}
                          title={lineLockedOnNewCustomer ? 'Keep line visible for reference' : 'Remove line'}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          {isNew && (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={24} /> : 'Create'}
            </Button>
          )}
        </Box>
      </Paper>

      </Box>
      <Dialog open={deleteConfirmOpen} onClose={() => !deleting && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Shipment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete shipment {slip?.identifier ?? slip?.packing_slip_number ?? id}. Invoices are not deleted. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteSlip} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PackingSlipDetail;
